import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import { CustomOption } from '@repo/toolkit/types'
import ActionButton from '@repo/ui/action-button'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import EditableInnerBox from '@repo/ui/editable-box'
import HourInput from '@repo/ui/hour-input'
import {
  BuildingThreeIcon,
  CarIcon,
  ClockIcon,
  DeleteIcon,
  PenIcon,
  PlusIcon
} from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Input from '@repo/ui/input'
import Loader from '@repo/ui/loader'
import Modal, { MODAL_CONTENT_CLASSNAME } from '@repo/ui/modal'
import SelectInput from '@repo/ui/select-input'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { toast } from 'sonner'

import { confirmMission } from '../../../api/missions/confirm-mission'
import { getMission } from '../../../api/missions/get-mission'
import { updateMission } from '../../../api/missions/update-mission'
import { getTimezones } from '../../../api/timezones/get-timezones'
import { getTripAddresses } from '../../../api/trip-addresses/get-trip-addresses'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../lib/hooks/use-translate'
import {
  MissionTypeEnum,
  missionTypeEnumMapping
} from '../../../lib/types/enums/mission/mission-type.enum'
import { missionTypeAvailabilityEnumMapping } from '../../../lib/types/enums/mission/mission-type-availability.enum'
import { staffQualificationEnumMapping } from '../../../lib/types/enums/staff/staff-qualifications.enum'
import { MissionTimeInput } from '../../../lib/types/models/mission/mission-input'
import { MissionProperties } from '../../../lib/types/models/mission/properties.mission'
import { Time } from '../../../lib/types/utils'
import { ItemNotFound } from '../../elements/item-not-found'
import LabelWithText from '../../elements/label-with-text'
import LoadingScreen from '../../elements/loading/loading-screen'
import MissionStaffTable from './affected-lists/mission-staff-table'

type Props = {
  missionId: number
  showStaffTable?: boolean
  mode?: 'view' | 'edit'
  onUpdated?: () => void
}

const MissionDetails = ({
  showStaffTable = false,
  mode = 'edit',
  missionId,
  onUpdated
}: Props) => {
  const queryClient = useQueryClient()
  const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const { t } = useTranslate()

  // Modal editing states - only used when modals are open
  const [editingArrivalTimes, setEditingArrivalTimes] = useState<MissionTimeInput[]>([])
  const [editingEndPlace, setEditingEndPlace] = useState<CustomOption<number>>()
  const [editingMeetingPlace, setEditingMeetingPlace] = useState<CustomOption<number>>()
  const [editingAppointmentTime, setEditingAppointmentTime] = useState<MissionTimeInput>()

  const { modalState, toggleModal, closeModel } = useModalManager<
    'edit-end-place' | 'edit-reservations'
  >(['edit-end-place', 'edit-reservations'])

  const { data, isFetching, isLoading } = useQuery({
    queryKey: ['mission-details', missionId],
    queryFn: () => getMission(missionId),
    enabled: !!missionId
  })

  const { data: tripAddresses } = useQuery({
    queryKey: ['trip-addresses'],
    queryFn: () => getTripAddresses()
  })

  const { data: timezones } = useQuery({
    queryKey: ['timezones'],
    queryFn: () => getTimezones()
  })

  const { mutate, isPending } = useMutation({
    mutationFn: confirmMission,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['mission-details', missionId]
      })
      onUpdated?.()
    }
  })

  const updateMissionMutation = useMutation({
    mutationFn: updateMission,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['mission-details', missionId]
      })
      onUpdated?.()
      toast.success('Mission mise à jour avec succès')
    }
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value)

    // Clear the previous timeout, if any
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current)
    }

    // Set a new timeout; when it expires, run the mutation
    debounceTimeout.current = setTimeout(() => {
      updateMissionMutation.mutate({
        id: missionId,
        requiredNumberOfStaff: value
      })
    }, 700)
  }

  // Combined reservations modal (appointment time, arrival times, meeting place)
  const handleOpenReservationsModal = () => {
    // appointment time
    setEditingAppointmentTime(
      data?.appointmentTime
        ? { time: DateFormatter.getFormattedTime(data.appointmentTime) }
        : { time: '' }
    )

    // arrival times
    if (data?.arrivalTimes && data.arrivalTimes.length > 0) {
      setEditingArrivalTimes(
        data.arrivalTimes.map((at) => ({
          id: at.id,
          time: DateFormatter.getFormattedTime(at.time),
          timeZone: at.timeZone
        }))
      )
    } else setEditingArrivalTimes([])

    // meeting place
    if (data?.meetingPlaceId && tripAddresses?.data) {
      const mp = tripAddresses.data.find((address) => address.id === data.meetingPlaceId)
      if (mp) setEditingMeetingPlace({ label: mp.label, value: mp.id })
      else setEditingMeetingPlace(undefined)
    } else setEditingMeetingPlace(undefined)

    toggleModal('edit-reservations')
  }

  const handleCancelReservations = () => closeModel('edit-reservations')

  const handleSaveReservations = () => {
    // parse appointment time
    const appointmentTime = editingAppointmentTime?.time
      ? (() => {
          const parts = editingAppointmentTime.time.split(':').map((p) => Number(p))
          if (parts.length !== 2 || Number.isNaN(parts[0]) || Number.isNaN(parts[1])) return
          return { hours: parts[0], minutes: parts[1] } as Time
        })()
      : undefined

    console.log('Saving reservations with values:', {
      appointmentTime,
      meetingPlaceId: editingMeetingPlace?.value,
      arrivalTimes: editingArrivalTimes.filter((at) => at.time)
    })

    updateMissionMutation.mutate(
      {
        id: missionId,
        appointmentTime: appointmentTime,
        meetingPlaceId: editingMeetingPlace?.value,
        times: editingArrivalTimes.filter((at) => at.time)
      },
      {
        onSuccess: () => {
          console.log('Mission updated successfully')
          queryClient.invalidateQueries({ queryKey: ['mission-details', missionId] })
          console.log('Closing modal')
          closeModel('edit-reservations')
        }
      }
    )
  }

  const handleAddArrivalTime = () => {
    setEditingArrivalTimes([...editingArrivalTimes, { time: '', timeZone: 'Europe/Paris' }])
  }

  const handleRemoveArrivalTime = (index: number) => {
    setEditingArrivalTimes(editingArrivalTimes.filter((_, i) => i !== index))
  }

  const handleUpdateArrivalTime = (index: number, field: 'time' | 'timeZone', value: string) => {
    setEditingArrivalTimes(
      editingArrivalTimes.map((at, i) => (i === index ? { ...at, [field]: value } : at))
    )
  }

  const handleOpenEndPlaceModal = () => {
    // Initialize from current data
    if (data?.endPlaceId && tripAddresses?.data) {
      const endPlace = tripAddresses.data.find((address) => address.id === data.endPlaceId)
      if (endPlace) {
        setEditingEndPlace({ label: endPlace.label, value: endPlace.id })
      } else {
        setEditingEndPlace(undefined)
      }
    } else {
      setEditingEndPlace(undefined)
    }
    toggleModal('edit-end-place')
  }

  const handleCancelEndPlace = () => {
    // Simply close - changes are discarded
    closeModel('edit-end-place')
  }

  const handleSaveEndPlace = () => {
    updateMissionMutation.mutate(
      {
        id: missionId,
        endPlaceId: editingEndPlace?.value
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['mission-details', missionId] })
          closeModel('edit-end-place')
        }
      }
    )
  }

  const hasNoStaffAffected = () => {
    if (!data) return true

    return (
      (data.type === MissionTypeEnum.STAFF && data.affectedStaffs.length === 0) ||
      (data.type === MissionTypeEnum.OFFICE && data.affectedLecStaffs.length === 0) ||
      (data.type === MissionTypeEnum.EXTERNAL && data.affectedExternalStaffs.length === 0)
    )
  }

  // const numberOfSteps =
  //   data?.tripBookings.reduce((total, booking) => total + booking.tripSteps.length, 0) ?? 0

  if (isLoading) return <LoadingScreen />
  if (!data || !missionId) return <ItemNotFound title="Mission non trouvée" />

  return (
    <div className="flex flex-col gap-4">
      <Box className="flex flex-col gap-4">
        {mode == 'edit' && (
          <div className="flex w-full justify-end ">
            <Button
              loading={isPending}
              label="Confimer les affectations"
              variant="ghost"
              onClick={() => mutate(missionId)}
              disabled={hasNoStaffAffected()}
            />
          </div>
        )}
        <EditableInnerBox
          title="Détails des réservations"
          className="rounded-xl bg-white"
          onEdit={handleOpenReservationsModal}
        >
          <Box className="bg-primary-light rounded-lg">
            <div className="grid grid-cols-6 items-center">
              <LabelWithText
                label="Date"
                data={<Badge text={DateFormatter.DDMMYYYY(data.date)} />}
                className="gap-1"
              />

              <LabelWithText
                label="Heure de RDV Mission"
                data={DateFormatter.getFormattedTime(data.appointmentTime)}
                className="gap-1"
              />
              <LabelWithText
                label="Heures de départ"
                data={MissionProperties.displayDepartureTimes(data)}
                className="gap-1"
              />
              <LabelWithText
                label="Heures d'arrivée"
                data={MissionProperties.displayArrivalTimes(data)}
                className="gap-1"
              />

              <LabelWithText
                label="Lieu de RDV"
                data={data.meetingPlace.label}
                className="gap-1"
              />
              {/* <LabelWithText
                label="Nombre d'étapes"
                data={<Badge text={numberOfSteps} />}
                className="gap-1"
              /> */}
            </div>
          </Box>
        </EditableInnerBox>
        <InnerBox title="Mission" className="rounded-xl">
          <Box className="bg-primary-light rounded-lg flex flex-col gap-2">
            <div className="grid grid-cols-6 gap-3 items-center">
              <LabelWithText
                label="Type"
                data={missionTypeEnumMapping[data.type].label}
                className="gap-1"
              />
              <LabelWithText label="Libellé" data={data.missionType.title} className="gap-1" />

              <Input
                label="Effectif requis"
                value={data.requiredNumberOfStaff}
                labelClassName="bg-primary-light"
                onChange={handleChange}
                type="number"
                allowNegative={false}
              />
              <div className="flex items-center gap-2">
                <LabelWithText
                  label="Lieu de fin de mission"
                  data={data.endPlace ? data.endPlace.label : '-'}
                  className="gap-1"
                />
                <ActionButton
                  variant="grey"
                  type="button"
                  icon={PenIcon}
                  size="x-small"
                  onClick={handleOpenEndPlaceModal}
                />
              </div>
              {data.convoyageMission && (
                <Badge
                  text={'Mission de convoyage'}
                  variant="warning"
                  className="text-nowrap"
                  icon={<CarIcon color={tailwindColors.secondary.DEFAULT} />}
                  iconPosition="start"
                />
              )}

              {data.hotelRequired && (
                <Badge
                  text={'Hôtel'}
                  icon={<BuildingThreeIcon color={tailwindColors.secondary.DEFAULT} />}
                  variant="warning"
                  iconPosition="start"
                />
              )}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <LabelWithText
                label="Qualifications requises"
                data={
                  data.qualifications
                    .map((qualification) => t(staffQualificationEnumMapping[qualification].label))
                    .join(', ') ?? '-'
                }
                className="gap-1"
              />
              <LabelWithText
                label="Disponibilité requise"
                data={
                  <Badge
                    text={
                      data.requiredAvailability
                        ? missionTypeAvailabilityEnumMapping[data.requiredAvailability]
                        : '-'
                    }
                  />
                }
                className="gap-1"
              />
            </div>

            <LabelWithText label="Description" data={data.description ?? '-'} className="gap-1" />
          </Box>
        </InnerBox>
      </Box>
      {data &&
        showStaffTable &&
        (isFetching ? <Loader center /> : <MissionStaffTable missionId={missionId} mode={mode} />)}

      <Modal
        isOpen={modalState['edit-end-place']}
        icon={BuildingThreeIcon}
        variant="confirmation"
        size="small"
        isLoading={updateMissionMutation.isPending}
        title="Modifier le lieu de fin de mission"
        actionButtonsOptions={{
          confirmText: 'Enregistrer',
          cancelText: 'Annuler',
          onConfirm: handleSaveEndPlace
        }}
        onCancel={handleCancelEndPlace}
      >
        <div className={MODAL_CONTENT_CLASSNAME}>
          <SelectInput
            label="Lieu de fin de mission"
            searchable
            options={
              tripAddresses?.data?.map((address) => ({
                label: address.label,
                value: address.id
              })) ?? []
            }
            selectedOption={editingEndPlace}
            dropDownPosition="above"
            onChange={(value) => {
              if (value) {
                const address = tripAddresses?.data?.find((a) => a.id === value)
                if (address) {
                  setEditingEndPlace({ label: address.label, value: address.id })
                }
              } else {
                setEditingEndPlace(undefined)
              }
            }}
            optional
          />
        </div>
      </Modal>

      <Modal
        isOpen={modalState['edit-reservations']}
        icon={ClockIcon}
        variant="confirmation"
        size="medium"
        isLoading={updateMissionMutation.isPending}
        title="Modifier les réservations"
        actionButtonsOptions={{
          confirmText: 'Enregistrer',
          cancelText: 'Annuler',
          onConfirm: handleSaveReservations
        }}
        onCancel={handleCancelReservations}
      >
        <div className={MODAL_CONTENT_CLASSNAME}>
          <div className="flex flex-col gap-4">
            <div>
              <Typography variant="secondary-body" color={tailwindColors.primary.DEFAULT}>
                Heure de RDV Mission
              </Typography>
              <div className="h-2" />
              <HourInput
                label="Heure de RDV"
                value={
                  editingAppointmentTime?.time
                    ? {
                        hours: Number(editingAppointmentTime.time.split(':')[0]),
                        minutes: Number(editingAppointmentTime.time.split(':')[1])
                      }
                    : undefined
                }
                onChange={(value) =>
                  setEditingAppointmentTime(
                    value ? { time: DateFormatter.getFormattedTime(value) } : { time: '' }
                  )
                }
              />
            </div>

            <div>
              <Typography variant="secondary-body" color={tailwindColors.primary.DEFAULT}>
                Lieu de RDV
              </Typography>
              <div className="h-2" />
              <SelectInput
                label="Lieu de RDV"
                searchable
                options={
                  tripAddresses?.data?.map((address) => ({
                    label: address.label,
                    value: address.id
                  })) ?? []
                }
                selectedOption={editingMeetingPlace}
                dropDownPosition="above"
                onChange={(value) => {
                  if (value) {
                    const address = tripAddresses?.data?.find((a) => a.id === value)
                    if (address)
                      setEditingMeetingPlace({ label: address.label, value: address.id })
                  } else setEditingMeetingPlace(undefined)
                }}
                optional
              />
            </div>
            <div>
              <Typography variant="secondary-body" color={tailwindColors.primary.DEFAULT}>
                Heures d'arrivée
              </Typography>
              <div className="h-2" />
              <div className="flex flex-col gap-3">
                {editingArrivalTimes.map((arrivalTime, index) => (
                  <div key={index} className="flex items-end gap-2">
                    <div className="flex-1">
                      <HourInput
                        label="Heure d'arrivée"
                        value={DateFormatter.formatTime(arrivalTime.time)}
                        onChange={(value) =>
                          handleUpdateArrivalTime(
                            index,
                            'time',
                            value ? DateFormatter.getFormattedTime(value) : ''
                          )
                        }
                      />
                    </div>
                    <div className="flex-1">
                      <SelectInput
                        searchable
                        label="Timezone"
                        options={
                          timezones?.map((timezone) => ({
                            label: timezone.name,
                            value: timezone.name
                          })) ?? []
                        }
                        dropDownPosition="above"
                        selectedOption={
                          arrivalTime.timeZone
                            ? { label: arrivalTime.timeZone, value: arrivalTime.timeZone }
                            : undefined
                        }
                        onChange={(value) =>
                          handleUpdateArrivalTime(index, 'timeZone', value || 'Europe/Paris')
                        }
                      />
                    </div>
                    <ActionButton
                      variant="danger"
                      type="button"
                      icon={DeleteIcon}
                      size="small"
                      onClick={() => handleRemoveArrivalTime(index)}
                    />
                  </div>
                ))}
                <Button
                  label="Ajouter une heure d'arrivée"
                  variant="secondary"
                  icon={PlusIcon}
                  onClick={handleAddArrivalTime}
                  type="button"
                />
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default MissionDetails
